// File: clientapp/src/components/UploadDocsPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import PageLayout from './PageLayout';
import {
    Container,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Button,
    CircularProgress,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    Snackbar,
    Alert
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { startConnection } from '../services/signalrService';

// UploadProgressDialog modal component
function UploadProgressDialog({ open, progress, timeElapsed, uploadedBytes, totalBytes, onCancel }) {
    const percent = totalBytes ? Math.round((uploadedBytes / totalBytes) * 100) : 0;
    const formatMB = (bytes) => (bytes / (1024 * 1024)).toFixed(2);

    return (
        <Dialog open={open} disableEscapeKeyDown>
            <DialogTitle>Uploading File</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ mb: 2 }}>
                    <LinearProgress variant="determinate" value={percent} />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        {percent}% complete
                    </Typography>
                </Box>
                <Typography variant="body2">
                    {formatMB(uploadedBytes)} MB of {formatMB(totalBytes)} MB uploaded.
                </Typography>
                <Typography variant="body2">
                    Time elapsed: {Math.floor(timeElapsed / 1000)} seconds
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} color="error">Cancel</Button>
            </DialogActions>
        </Dialog>
    );
}

export default function UploadDocsPage() {
    const [selectedFile, setSelectedFile] = useState(null);
    const [activeUploads, setActiveUploads] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [connection, setConnection] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadedBytes, setUploadedBytes] = useState(0);
    const [totalBytes, setTotalBytes] = useState(0);
    const [timeElapsed, setTimeElapsed] = useState(0);
    const [progressDialogOpen, setProgressDialogOpen] = useState(false);
    const xhrRef = useRef(null);
    const progressIntervalRef = useRef(null);
    const navigate = useNavigate();

    // Initialize SignalR connection
    useEffect(() => {
        async function initConnection() {
            const conn = await startConnection();
            setConnection(conn);
        }
        initConnection();
    }, []);

    // Subscribe to upload status updates.
    useEffect(() => {
        if (connection) {
            connection.on("UploadStatusUpdate", update => {
                // Expected update: { workflowInstanceId, status, details, additionalData }
                let classificationResultId = null;
                let explicitStatus = update.status;
                if (update.additionalData) {
                    try {
                        const extra = JSON.parse(update.additionalData);
                        if (extra.Status) {
                            explicitStatus = extra.Status;
                        }
                        if (extra.ClassificationId) {
                            classificationResultId = extra.ClassificationId;
                        }
                    } catch (err) {
                        console.error("Error parsing additionalData:", err);
                    }
                }
                setActiveUploads(prev =>
                    prev.map(upload =>
                        upload.workflowInstanceId === update.workflowInstanceId
                            ? {
                                ...upload,
                                status: explicitStatus,
                                details: update.details,
                                classificationResultId: classificationResultId || upload.classificationResultId
                            }
                            : upload
                    )
                );
            });
        }
    }, [connection]);

    const onDrop = useCallback(acceptedFiles => {
        if (acceptedFiles.length > 0) {
            setSelectedFile(acceptedFiles[0]);
        }
    }, []);
    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

    // Cancel upload handler
    const cancelUpload = () => {
        if (xhrRef.current) {
            xhrRef.current.abort();
            setProgressDialogOpen(false);
            setErrorMessage("Upload canceled.");
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setErrorMessage("Please select a file to upload.");
            return;
        }
        setIsUploading(true);
        setErrorMessage("");
        const formData = new FormData();
        formData.append('file', selectedFile);

        const xhr = new XMLHttpRequest();
        xhrRef.current = xhr;
        xhr.open("POST", "/api/uploaddocs/upload", true);
        xhr.withCredentials = true;

        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                setTotalBytes(event.total);
                setUploadedBytes(event.loaded);
                setUploadProgress(Math.round((event.loaded / event.total) * 100));
            }
        };

        xhr.onloadstart = () => {
            setProgressDialogOpen(true);
            setTimeElapsed(0);
            progressIntervalRef.current = setInterval(() => {
                setTimeElapsed(prev => prev + 1000);
            }, 1000);
        };

        xhr.onload = () => {
            clearInterval(progressIntervalRef.current);
            setProgressDialogOpen(false);
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    const response = JSON.parse(xhr.responseText);
                    setActiveUploads(prev => [
                        ...prev,
                        {
                            workflowInstanceId: response.workflowInstanceId,
                            fileName: response.fileName,
                            status: "Uploading",
                            details: "",
                            classificationResultId: null
                        }
                    ]);
                } catch (e) {
                    setErrorMessage("Error parsing server response.");
                }
            } else {
                setErrorMessage("Upload failed.");
            }
            setIsUploading(false);
        };

        xhr.onerror = () => {
            clearInterval(progressIntervalRef.current);
            setProgressDialogOpen(false);
            setErrorMessage("Upload failed due to a network error.");
            setIsUploading(false);
        };

        xhr.onabort = () => {
            clearInterval(progressIntervalRef.current);
            setProgressDialogOpen(false);
            setIsUploading(false);
        };

        xhr.send(formData);
    };

    const handleDetails = (upload) => {
        if (upload.classificationResultId) {
            navigate(`/classification-results/${upload.classificationResultId}`);
        }
    };

    const handleCloseSnackbar = () => {
        setErrorMessage("");
    };

    return (
        <PageLayout
            icon={<CloudUploadIcon style={{ fontSize: 48 }} />}
            title="Upload Documents"
            subTitle="Drag and drop a document and click upload."
            breadcrumb={[
                { label: "Home", to: "/" },
                { label: "Documents", to: "/documents" },
                { label: "Upload Docs" }
            ]}
        >

            <Container maxWidth="md" sx={{ mt: 2 }}>

            <div {...getRootProps()} style={{
                border: '2px dashed #ccc',
                padding: '20px',
                textAlign: 'center',
                backgroundColor: isDragActive ? '#eee' : '#fff'
            }}>
                <input {...getInputProps()} />
                {isDragActive ? <p>Drop the file here...</p> : <p>Drag and drop a file here, or click to select one</p>}
            </div>
            {selectedFile && (
                <div style={{ marginTop: '10px' }}>
                    <strong>Selected File:</strong> {selectedFile.name}
                </div>
            )}
            <Button variant="contained" onClick={handleUpload} disabled={isUploading} sx={{ mt: 2 }}>
                {isUploading ? "Processing..." : "Upload"}
            </Button>
            <div style={{ marginTop: '20px' }}>
                <h3>Active Uploads</h3>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Filename</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Icon</TableCell>
                            <TableCell>Details</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {activeUploads.map(upload => (
                            <TableRow key={upload.workflowInstanceId}>
                                <TableCell>{upload.fileName}</TableCell>
                                <TableCell>{upload.status}</TableCell>
                                <TableCell>
                                    {upload.status === "Uploading" || upload.status === "Processing" ? (
                                        <CircularProgress size={24} />
                                    ) : upload.status === "Completed" ? (
                                        <CheckCircleIcon sx={{ color: 'green', fontSize: 24 }} />
                                    ) : null}
                                </TableCell>
                                <TableCell>
                                    {upload.status === "Completed" && upload.classificationResultId ? (
                                        <Button variant="outlined" onClick={() => handleDetails(upload)}>
                                            Details
                                        </Button>
                                    ) : (
                                        <Button variant="outlined" disabled>
                                            {upload.status === "Error" ? "Error" : "Processing"}
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
            <UploadProgressDialog
                open={progressDialogOpen}
                progress={uploadProgress}
                timeElapsed={timeElapsed}
                uploadedBytes={uploadedBytes}
                totalBytes={totalBytes}
                onCancel={cancelUpload}
            />
            <Snackbar
                open={!!errorMessage}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                    {errorMessage}
                </Alert>
                </Snackbar>

            </Container>
        </PageLayout>
    );
}
